<template>
  <div class="popup p-4 z-[11]" :class="{ 'popup--fullscreen': fullScreen }">
    <slot name="outside" />
    <div class="m-auto flex size-full flex-col justify-center">
      <div ref="content" class="popup__content w-full mx-auto" :style="{ maxWidth }">
        <div v-if="!hideTitle" class="popup__content-header">
          <div class="popup__content-header-title" :class="{ 'pl-10': closeable }">
            <slot name="header" />
          </div>
          <UIIcon
            :class="{ 'popup__content-header-close--hidden': !closeable }"
            class="popup__content-header-close"
            name="big-close"
            @click="handleClose"
          />
        </div>
        <div class="popup__content-body">
          <slot name="body" />
        </div>
        <div
          v-if="!hideButtons"
          class="popup__content-buttons"
          :class="{ 'popup__content-buttons-centered': centeredButtons }"
        >
          <slot name="buttons">
            <UIButton v-if="!hideCloseButton" id="popup-close" compact variant="secondary" @click="handleClose">
              {{ closeText }}
            </UIButton>
            <UIButton
              id="popup-apply"
              compact
              :loading="loading"
              class="popup__content-buttons-apply"
              @click="emit('apply')"
            >
              {{ buttonText }}
            </UIButton>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// TODO: this component should be refactored due to complicated logic =|
const props = withDefaults(
  defineProps<{
    closeable?: boolean
    closeText?: string
    buttonText?: string
    loading?: boolean
    disableBodyLock?: boolean
    hideCloseButton?: boolean
    hideTitle?: boolean
    hideButtons?: boolean
    fullScreen?: boolean
    visible?: boolean
    centeredButtons?: boolean
    maxWidth?: CSSStyleDeclaration['maxWidth']
  }>(),
  {
    closeable: false,
    // TODO: rename it
    closeText: 'Close',
    buttonText: 'Apply',
    disableBodyLock: false,
    hideCloseButton: false,
    centeredButtons: false,
    maxWidth: '440px',
  },
)

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'apply'): void
}>()

const content = ref<HTMLDivElement | null>(null)

const { isMobile } = useAppBreakpoints()

onClickOutside(content, () => {
  if (isMobile) return

  if (props.closeable) {
    handleClose()
  }
})

onKeyStroke('Escape', () => {
  if (props.closeable) {
    handleClose()
  }
})

const { lock, unlock } = useBodyScrollLock()

const handleClose = () => {
  emit('close')
  unlock()
}

onMounted(() => {
  if (!props.disableBodyLock) {
    lock()
  }
})

onBeforeUnmount(() => {
  unlock()
})
</script>

<style scoped lang="scss">
@import '@/styles/mixins.scss';
.popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(3px);

  &--fullscreen {
    position: fixed;
    z-index: 1000;
    padding: 0;

    .popup__content {
      @media screen and (max-width: 1023px) {
        width: 100vw;
        height: 100dvh;
        max-width: unset;
        border-radius: unset;
      }
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    background: white;
    padding: 32px;
    border-radius: 16px;
    box-shadow: 0px 2px 10px 0px rgba(15, 21, 29, 0.12);

    @media screen and (max-width: 1023px) {
      max-width: calc(100% - 32px);
      padding: 16px;
      border-radius: 12px;
    }

    &-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      text-align: center;
      margin-bottom: 16px;

      @media screen and (max-width: 1023px) {
        margin-left: 0;
        margin-bottom: 8px;
      }

      &-title {
        width: 100%;
        @include headline3();

        @media screen and (max-width: 1023px) {
          @include subhead3();
          line-height: 20px;
          text-align: left;
          color: $black-70;
        }
      }

      &-close {
        margin-left: 16px;
        display: block;
        cursor: pointer;

        &--hidden {
          visibility: hidden;
          opacity: 0;
        }
      }
    }

    &-body {
      @include body2();
      text-align: center;
      margin-bottom: 24px;
    }

    &-buttons {
      display: flex;
      justify-content: flex-end;
      gap: 16px;

      @media screen and (max-width: 1023px) {
        margin-top: auto;
        .button {
          flex-grow: 1;
        }
      }

      &-centered {
        justify-content: center;
      }
    }
  }
}
</style>
